<template>
  <div v-if="parentEntity" class="mt-3">
    <right-panel class="float-right" id="sidebar-new-data" width="600px" icon="" @hidden="rightPanelClosed">
      <div class="pl-5 pr-5">
        <Form @onSave="onDataSaved" />
      </div>
    </right-panel>
    <!-- TKP-203: yeni eklenen veriye süreçteki başka bir verinin özelliklerini kopyalama önerisi -->
    <copy-features-dialog v-if="parentEntity.department" ref="copyDialog" :department="parentEntity.department" :process="parentEntity" :target-node-ids="copyTargets" @copied="paginate" />

    <right-panel class="float-right" id="sidebar-info-data" width="800px">
      <div class="pl-5 pr-5" v-if="infoItem">
        <h3 class="mb-5">
          <i class="bx bx-info-circle align-middle font-size-24 mr-2"></i>
          <span class="  text-primary">{{ infoItem.name | translate }}</span>
          {{ $t("data.info") }}
        </h3>

        <content-loading v-if="isInfoLoading" />
        <div v-else>
          <h5>{{ $t("data.used-places") }}</h5>
          <table v-if="info && info.docs.length > 0" class="table table-border table-striped">
            <thead class="bg-dark text-white">
              <tr>
                <th>{{ $t("department.text") }}</th>
                <th>{{ $t("process.text") }}</th>
                <th>{{ $t("datasource.text") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="i in info.docs" :key="'info' + i._id">
                <td>{{ i.department.name | translate }}</td>
                <td>{{ i.process.name | translate }}</td>
                <td>{{ sourceNames(i) }}</td>
              </tr>
            </tbody>
          </table>
          <empty v-else width="200px">
            <h6 class="text-center">{{ $t("common.no-hierarchy") }}</h6>
          </empty>
        </div>
        <div v-if="info" class="row">
          <div class="col">
            <ul class="pagination pagination-rounded mb-0">
              <b-pagination v-model="infoRequest.options.page" :total-rows="info.totalDocs" :per-page="info.limit" limit="10" size="sm"></b-pagination>
            </ul>
          </div>
        </div>
      </div>
    </right-panel>

    <!-- Begin : Inventory (düğüm) — TKP-188: dört seviyede TEK panel (birim/süreç kutularıyla aynı bileşen), kapsam bu düğüm -->
    <template>
      <right-panel class="float-right" id="sidebar-data-inventory" width="1400px" icon="" @hidden="inventoryPanelClosed">
        <HierarchyInventoryView v-if="selectedBox && parentEntity" ref="inventoryPanel" :department="parentEntity.department" :process="parentEntity" :node="selectedBox" data-test="data-inventory-view" @onNodeSaved="onNodeSaved" />
      </right-panel>
    </template>
    <!-- End : Inventory -->

    <div class="ds-card">
      <div class="ds-card--title justify-content-between">
        <span>
          <i class="iconoir-center-align align-middle mr-3" style="color: #383A55; font-size: 1.5rem !important;"></i>
          <span
            v-html="
              $t('process-data.owned-list', {
                department: $options.filters.translate(parentEntity.department && parentEntity.department.name),
                process: $options.filters.translate(parentEntity.name),
              })
            "
          >
          </span>
        </span>
      </div>
      <div>
        <div class="d-flex align-items-center mb-4" style="gap: 1rem;">
          <div class="search2" style=" width: 18.50rem;">
            <div class="search2--icon"><i class="iconoir-search"></i></div>
            <input class="search2--input" type="text" :placeholder="$t('common.search')" v-model="nameFilter" />
          </div>
          <!-- TKP-137: arşiv görünümü — arşive alınan düğümler varsayılan listede yok -->
          <b-form-checkbox v-model="showArchived" switch data-test="show-archived">{{ $t("process-data.show-archived") }}</b-form-checkbox>
        </div>
      </div>

      <content-loading v-if="isLoading" />
      <div v-else-if="pagination" class="d-flex" style="gap: 1.25rem;flex-wrap: wrap;">
        <div v-for="item in pagination.docs" :key="item._id" class="ds-card--item" :class="selectedBoxStyle(item)" @click="selectBox(item)">
          <div class="text">
            {{ item.data && item.data.name | translate }}
            <draft-badge entity-type="NODE" :entity-id="item._id" />
            <span v-if="item.archived" class="badge badge-secondary ml-1" data-test="archived-badge">{{ $t("process-data.archived") }}</span>
            <div class="small mt-1" :class="selectedBox && selectedBox._id == item._id ? 'text-white-50' : 'text-muted'">
              <span v-if="item.effectiveCategory" v-b-tooltip.hover :title="$t('data-category.text')">
                <i class="bx bx-layer"></i> {{ item.effectiveCategory.name | translate }}
                <i v-if="item.category" class="bx bx-edit-alt" v-b-tooltip.hover :title="$t('process-data.category-override')"></i>
              </span>
              <!-- TKP-170 md.33: kaynak adları kutuda yazmaz (kaynak envanter özelliği, panelde görülür).
                   TKP-131: kaynaklar kendi taslağımda değiştiyse taslak işareti + taslaktaki liste kalır (onaylanınca envantere yazılır). -->
              <template v-if="draftDatasources(item)">
                <br />
                <span v-b-tooltip.hover :title="$t('approval.badge.draft')" data-test="draft-datasources">
                  <i class="bx bx-edit"></i> {{ namesOf(draftDatasources(item)) || $t("process-data.no-datasource") }}
                </span>
              </template>
            </div>
          </div>
          <div class="actions d-flex justify-content-center">
            <div class="action" v-if="hasRemovePermission" @click="deleteById(item)" v-on:click.stop v-b-tooltip.hover :title="$t('common.delete')">
              <i class="iconoir-trash"></i>
            </div>
            <!-- TKP-137: arşive al / geri al (silme ile aynı yetki; onay açıkken taslağa düşer) -->
            <div class="action" v-if="hasRemovePermission" @click="toggleArchive(item)" v-on:click.stop v-b-tooltip.hover :title="item.archived ? $t('process-data.unarchive') : $t('process-data.archive') + ' — ' + $t('process-data.archive-hint')" :data-test="item.archived ? 'unarchive' : 'archive'">
              <i :class="item.archived ? 'iconoir-undo' : 'iconoir-archive'"></i>
            </div>
            <!-- TKP-241 p.8: organizasyon geneli bilgi — Birim / Süreç Yöneticisi görmez -->
            <div v-if="currentAccountCan('ENTITY_DATA_USED_PLACES__CAN_VIEW')" class="action" data-test="data-used-places" @click="fetchInfo(item.data)" v-on:click.stop v-b-toggle="'sidebar-info-data'" v-b-tooltip.hover :title="$t('data.used-places')">
              <i class="iconoir-info-circle"></i>
            </div>
            <div class="action" @click="edit(item)" v-on:click.stop v-b-tooltip.hover :title="$t('common.edit')">
              <i class="iconoir-edit-pencil"></i>
            </div>
            <div class="action" @click="showInventoryPanel(item)" v-on:click.stop v-b-tooltip.hover :title="$t('inventory.filter.inventory-features')">
              <i class="iconoir-network-right"></i>
            </div>
          </div>
        </div>

        <!-- TKP-131: bu sürece taslağımda eklediğim veriler (envantere onayla yazılır; TKP-200 m.19: envanter özellikleri paneli taslakta da açılır) -->
        <!-- TKP-189: gölge veri seçilebilir → ilgili kişi kutusu gölge düğümün altını gösterir (TKP-186 gölge satırlar) -->
        <div v-for="node in shadowNodes" :key="'shadow-' + node._id" class="ds-card--item" style="opacity: .8; border-style: dashed;" :class="selectedBoxStyle(node)" data-test="shadow-node" @click="selectBox(node)">
          <div class="text">
            {{ node.name | translate }}
            <b-badge variant="info" class="ml-1"><i class="bx bx-edit"></i> {{ $t(node.packageStatus === "SUBMITTED" ? "approval.badge.submitted" : "approval.badge.draft") }} · {{ $t("approval.new-record") }}</b-badge>

          </div>
          <!-- TKP-200 m.19: taslak veride de envanter özellikleri -->
          <div class="actions d-flex justify-content-center">
            <div class="action" data-test="shadow-node-inventory" @click="showInventoryPanel(node)" v-on:click.stop v-b-tooltip.hover :title="$t('inventory.filter.inventory-features')">
              <i class="iconoir-network-right"></i>
            </div>
          </div>
        </div>

        <div class="ds-card--item2" @click="openRightPanel">
          <i class="iconoir-plus" style="font-size: 1.75rem;"></i>
        </div>
      </div>

      <div v-if="pagination" class="row mt-4">
        <div class="col">
          <ul class="pagination pagination-rounded mb-0">
            <b-pagination v-model="request.options.page" :total-rows="pagination.totalDocs" :per-page="pagination.limit" limit="10" size="sm"></b-pagination>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DraftBadge from "@/modules/approval/draft-badge"; // TKP-104
import HierarchyInventoryView from "@modules/inventory/component/hierarchy/hierarchy-inventory-view"; // TKP-188
import CopyFeaturesDialog from "@modules/inventory/component/hierarchy/copy-features-dialog"; // TKP-203
import Form from "@modules/process-data/organization-process-data-form";
import { sourceNamesOf } from "@/modules/process-data/info-row";
import { draftDatasourcesOf, shadowNodesOf } from "@/modules/approval/approval"; // TKP-131

/**
 * TKP-27 / Faz 6 dilim 6.1 — veri kutusu artık süreç×veri DÜĞÜMÜ (`/process-data/paginate`, süreç altında). Kaynak seçimi
 * gerekmez; kaynaklar kartta özellik olarak görünür (K1), kategori efektif kategoridir (K4). Saklama süresi düğümde
 * düzenlenir; imha yöntemi / erişim kısıtı EKLEME 6.2'deki tek forma taşındı (burada liste + silme).
 */
export default {
  components: { DraftBadge, Form, HierarchyInventoryView, CopyFeaturesDialog },
  data() {
    return {
      showArchived: false, // TKP-137
      copyTargets: [], // TKP-203
      nameFilter: undefined,
      request: { options: { page: 1, limit: 17 }, query: {} },
      infoRequest: { options: { page: 1, limit: 10, sort: { name: "asc" } }, query: {} },
    };
  },
  computed: {
    parentEntity() {
      return this.$store.getters["entityBoxes/process"];
    },
    pagination() {
      return this.$store.getters["organizationProcessData/pagination"];
    },
    isLoading() {
      return this.$store.getters["organizationProcessData/isLoading"];
    },
    hasRemovePermission() {
      return true;
    },
    infoItem() {
      return this.$store.getters["organizationData/infoItem"];
    },
    info() {
      return this.$store.getters["organizationData/info"];
    },
    isInfoLoading() {
      return this.$store.getters["organizationData/isInfoLoading"];
    },
    selectedBox() {
      return this.$store.getters["entityBoxes/data"];
    },
    drafts() {
      return this.$store.getters["approval/drafts"];
    },
    shadowNodes() {
      return this.parentEntity ? shadowNodesOf(this.drafts, this.parentEntity._id, this.currentAccount && this.currentAccount._id) : [];
    },
  },
  watch: {
    showArchived() {
      this.paginate(); // TKP-137
    },
    async nameFilter(n, o) {
      this.debounce(() => this.handleNameFilterChanged(n, o));
    },
    request: {
      deep: true,
      immediate: true,
      handler() {
        this.debounce(() => this.paginate());
      },
    },
    parentEntity() {
      this.debounce(() => this.paginate());
    },
    infoRequest: {
      deep: true,
      handler() {
        this.debounce(() => this.fetchInfo());
      },
    },
  },
  methods: {
    sourceNames(row) {
      return sourceNamesOf(row, this.$options.filters.translate); // TKP-27: panel satırı düğüm, kaynaklar `datasources[]`
    },
    draftDatasources(item) {
      return draftDatasourcesOf(this.drafts, item._id, this.currentAccount && this.currentAccount._id);
    },
    namesOf(list) {
      return list.map((d) => this.$options.filters.translate(d.name)).filter(Boolean).join(", ");
    },
    async handleNameFilterChanged(n) {
      this.$store.commit("entityBoxes/clearFrom", "data");
      this.$store.commit("organizationProcessData/selectedEntity", undefined);
      if (n) {
        this.request.query = { ...this.request.query, name: { value: n } };
      } else {
        this.request.query["name"] = null;
        delete this.request.query["name"];
      }
    },
    /** TKP-203: veri eklendi → süreçte başka veri varsa "özellikleri kopyala" önerisi (Evet → pencere bu verilerle). */
    async onDataSaved(event) {
      await this.paginate();
      const ids = ((event && event.savedIds) || []).map(String);
      const others = [...(this.pagination?.docs ?? []), ...this.shadowNodes].filter((d) => !ids.includes(String(d._id)) && !d.__draft);
      if (!ids.length || !others.length) return;
      if (!(await this.confirm(this.$t("inventory.copy.offer-node", { count: ids.length })))) return;
      this.copyTargets = ids;
      await this.$nextTick();
      this.$refs.copyDialog && this.$refs.copyDialog.open();
    },
    openRightPanel() {
      this.$store.commit("organizationProcessData/editedEntity", undefined);
      this.$root.$emit("bv::toggle::collapse", "sidebar-new-data");
    },
    selectedBoxStyle(item) {
      if (this.selectedBox?._id == item._id) return "bg-main text-white";
    },
    async paginate() {
      if (!this.parentEntity) return;
      const request = { ...this.request };
      request.query = { ...request.query, process: { value: { _id: this.parentEntity._id }, isObject: true }, ...(this.showArchived ? { archived: { value: true, isObject: true } } : {}) }; // TKP-137
      await this.$store.dispatch("organizationProcessData/paginate", request);
    },
    /** TKP-137: arşive al / geri al; onay açıkken kapsamlı yönetici için taslağa düşer. */
    async toggleArchive(item) {
      const archived = !item.archived;
      const ok = await this.confirm(this.$t(archived ? "process-data.archive-confirm" : "process-data.unarchive-confirm", { name: this.$options.filters.translate(item.data?.name) }));
      if (!ok) return;
      try {
        const response = await this.$store.dispatch("organizationProcessData/archive", { _id: item._id, archived });
        if (this.selectedBox?._id == item._id) this.$store.commit("entityBoxes/clearFrom", "data");
        if (response && response.data && response.data.draft) {
          this.$toast.success(this.$t("approval.saved-to-draft"));
          await this.$store.dispatch("approval/fetch").catch(() => undefined);
        } else this.$toast.success(this.$t(archived ? "process-data.archived-done" : "process-data.unarchived-done"));
        this.paginate();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async deleteById(item) {
      const ok = await this.confirm(this.$t("alert.approve"));
      if (!ok) return;
      try {
        await this.$store.dispatch("organizationProcessData/delete", item._id);
        if (this.selectedBox?._id == item._id) this.$store.commit("entityBoxes/clearFrom", "data");
        this.paginate();
        this.$toast.success("'" + this.$options.filters.translate(item.data?.name) + "' Silindi");
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error, { customMessage: "'" + this.$options.filters.translate(item.data?.name) + "' Silinemedi" });
      }
    },
    edit(item) {
      this.$store.commit("organizationProcessData/editedEntity", item);
      this.$root.$emit("bv::toggle::collapse", "sidebar-new-data");
    },
    rightPanelClosed() {
      this.paginate();
      this.$store.commit("organizationProcessData/editedEntity", undefined);
    },
    selectBox(item) {
      this.$store.dispatch("entityBoxes/selectData", item);
    },
    fetchInfo(item) {
      if (!item) item = this.infoItem;
      if (!item) return;
      this.$store.dispatch("organizationData/fetchInfo", { item, pagination: this.infoRequest });
    },
    //inventory (TKP-188: bölümler ve yükleme HierarchyInventoryView'da, dört seviyede aynı)
    showInventoryPanel(item) {
      this.$store.commit("entityBoxes/data", item);
      this.$store.commit("organizationProcessData/selectedEntity", item);
      this.$root.$emit("bv::toggle::collapse", "sidebar-data-inventory");
    },
    /** Panelin `node` prop'u seçili kutudan (`entityBoxes/data`) gelir; liste yenilenince seçim de taze dokümanla yenilenir —
        yoksa kaydedilen tedbirler kutu yeniden seçilene kadar panelde görünmezdi. */
    async onNodeSaved() {
      await this.paginate();
      const fresh = (this.pagination?.docs ?? []).find((d) => String(d._id) === String(this.selectedBox?._id));
      if (!fresh) return;
      // `selectData` aynı kimlikte seçimi KALDIRIR (kutuya ikinci tıklama); burada seçim korunup nesne tazelenir
      this.$store.commit("entityBoxes/data", fresh);
      this.$store.commit("organizationProcessData/selectedEntity", fresh);
    },
    inventoryPanelClosed() {
      this.$refs.inventoryPanel?.releaseBulkScope();
      this.paginate();
    },
  },
};
</script>

<style lang="scss" scoped>
@import url("../organization-entities-view.scss");
</style>

<style scoped>
.when-opened,
.when-closed {
  font-size: 20px;
}
.collapsed > .when-opened,
:not(.collapsed) > .when-closed {
  display: none;
}
</style>
